import type { TaxReturn, ContactInfo, AccountInfo } from '../../types';

/**
 * S2 — RELINK, NOT RENAME.
 *
 * Builds the update payload for pointing a tax return at a different CRM record.
 *
 * The bug this closes: linking used to set only `contactId`/`contactName`, so the return went
 * on DISPLAYING the previous person's name beside a freshly-linked contact. The only control
 * on screen that could fix that display was the client-name box — and before PR #71, editing
 * it PATCHed the SHARED Dataverse contact. That is the path by which a relink became a rename,
 * and it is why ~29 identity substitutions reached production between 2026-02 and 2026-04.
 *
 * The spouse link handler has always set all of its fields together, which is very likely why
 * no one ever hand-edited spouse names. These helpers make the client path symmetric.
 *
 * S2 SECOND HALF (2026-08-12) — `clientId` NOW MOVES, and this comment used to say the opposite.
 *
 * It previously read *"clientId is deliberately absent from both payloads… the update endpoint
 * rejects it by design"*. That was true, and it is no longer: the forward-only relink was ruled
 * and shipped, and `ApplyUpdates` now accepts `clientid` **when accompanied by `contactid` in the
 * same request**. Identity moves as a set or not at all.
 *
 * What has NOT changed is the reason the old comment existed. `clientId` is still not a display
 * field — ~123 call sites key outreach history, invoices, inquiries and message threads off it.
 * Forward-only means **none of that follows the relink**: it stays with the client it was created
 * for, which is correct when a return genuinely changes client and safe when the link was wrong
 * from the start. The backend records the old value in `previousClientId` so the resulting split
 * is reconstructable.
 *
 * ⚠ WHICH ID GOES IN `clientId` — measured against 200 production returns, not inferred:
 *   - individual: `clientId === contactId` in 160/160 — both are the Dataverse CONTACT guid.
 *   - business:   `clientId !== contactId` in  40/40 — `clientId` is the ACCOUNT (the company),
 *                 `contactId` is a person at that company.
 * That is the id-space rule these builders now encode, and `idSpace` tests below pin it. Putting
 * the wrong guid here would orphan every record keyed on the old one — the trap that cost
 * ptc-supervision two failed `USE_SQL_CORE` attempts.
 *
 * Extracted as pure functions (same pattern as `spouseAffordance.ts` from PR #72) so the
 * regression is pinned by name rather than through the search UI.
 */

/**
 * Fields a relink is allowed to touch.
 *
 * `clientId` is present as of the S2 second half. It is here **only** alongside `contactId` —
 * the two are never separable, because a return pointing at one client and a different person is
 * the id-space mismatch behind the 2026-08-04 contact-capture incident.
 */
export type RelinkPayload = Pick<
  TaxReturn,
  'clientId' | 'contactId' | 'contactName' | 'clientName' | 'clientEmail' | 'clientPhone'
>;

/**
 * Relink to an individual Dataverse contact.
 * Existing email/phone are preserved when the chosen contact carries none — a relink should
 * never blank contact details it has nothing better to put there.
 */
export function buildContactRelinkPayload(
  contact: ContactInfo,
  current: Pick<TaxReturn, 'clientEmail' | 'clientPhone'>
): RelinkPayload {
  return {
    // Individual returns key BOTH ids on the person: measured clientId === contactId in
    // 160/160 sampled individual returns. There is no separate account for an individual.
    clientId: contact.contactId,
    contactId: contact.contactId,
    contactName: contact.fullName,
    clientName: contact.fullName,
    clientEmail: contact.email || current.clientEmail,
    clientPhone: contact.phone || contact.mobilePhone || current.clientPhone,
  };
}

/** Relink to a Dataverse account (business returns). */
export function buildAccountRelinkPayload(
  account: AccountInfo,
  current: Pick<TaxReturn, 'clientEmail' | 'clientPhone'>
): RelinkPayload {
  return {
    // Business returns key clientId on the ACCOUNT — measured clientId !== contactId in 40/40
    // sampled business returns, with clientId naming the company and contactId a person there.
    clientId: account.accountId,
    // ⚠ PRE-EXISTING AND DELIBERATELY LEFT ALONE: this writes the ACCOUNT guid into contactId,
    // which is the person's id-space. Every business return sampled in production carries a real
    // person there (e.g. clientName "FLIAVIKAN INC" with contactName "Walter Cinalli"), so a
    // relink through this builder makes contactId diverge from that shape. `primaryContactId`
    // exists on AccountInfo and is the plausible correct source, but it is optional and changing
    // this is a behaviour change OUTSIDE the S2 ruling — filed rather than fixed in passing.
    contactId: account.accountId,
    contactName: account.name,
    clientName: account.name,
    clientEmail: account.email || current.clientEmail,
    clientPhone: account.phone || current.clientPhone,
  };
}
